import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  Input,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  Article,
  articlesApi,
  UpdateArticleMetadataRequest,
} from '@texas/api/endpoints/articlesApi';
import {
  contactsApi,
  defaultSearchQuery,
  ContactListItem,
} from '@texas/api/endpoints/contactsApi';
import {
  customersApi,
  CustomerConcept,
} from '@texas/api/endpoints/customersApi';
import {
  ProductGroup,
  productGroupsApi,
} from '@texas/api/endpoints/metadata/productGroupsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { angularEvents } from '@bridge/angularEvents';
import { reactEvents } from '@bridge/reactEvents';
import { useEffect, useState } from 'react';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { ManagementCard } from './ManagementCard';
import { ArticleBranchesCard } from './ArticleBranchesCard';
import { useTranslation } from 'react-i18next';
import { usePendingChanges } from '@texas/hooks/usePendingChanges';
import { PendingChangesAction } from './PendingChangesAction';
import {
  PendingChangesIndicator,
  PendingChangesLabel,
} from '@texas/components/shared/form/PendingChangesIndicator';
import { Controller, useForm } from 'react-hook-form';
import { NewConcept } from '@texas/components/customer/newConcept/NewConcept';
import { articleDescriptionMaxLength } from '@texas/resources/constants';

interface Props {
  article: Article;
  onUpdated: () => void;
  onCopy?: () => void;
}

const getMetadataForm = (article: Article): UpdateArticleMetadataRequest => ({
  name: article.name,
  itemCode: article.itemCode,
  conceptId: article.conceptId === 0 ? null : article.conceptId ?? null,
  productGroupId:
    article.productGroupId === 0 ? null : article.productGroupId ?? null,
  contactId: article.contactId === 0 ? null : article.contactId ?? null,
  isVariable: article.isVariable,
  collectionItem: article.collectionItem,
  ownerId: article.ownerId,
});

export function GeneralManagement({ article, onUpdated, onCopy }: Props) {
  const toast = useToast();
  const { t } = useTranslation();
  const form = useForm<UpdateArticleMetadataRequest>({
    defaultValues: getMetadataForm(article),
  });
  const {
    control,
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
  } = form;
  const {
    getFieldStatus,
    hasPendingChanges,
    markChangesSaved,
    resetPendingChanges,
  } = usePendingChanges(form);
  const itemCode = watch('itemCode');
  const description = watch('name');
  const [concepts, setConcepts] = useState<CustomerConcept[]>([]);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [contacts, setContacts] = useState<ContactListItem[]>([]);
  const { request, loading, error } = useApiRequest(articlesApi.updateMetadata);
  const {
    request: updateLifecycle,
    loading: lifecycleLoading,
    error: lifecycleError,
  } = useApiRequest(
    article.archived ? articlesApi.restore : articlesApi.archive,
  );

  useEffect(() => {
    const metadataForm = getMetadataForm(article);
    resetPendingChanges(metadataForm);
  }, [article, resetPendingChanges]);

  useEffect(() => {
    if (!article.customerId) return;
    customersApi.getConcepts(article.customerId).then((response) => {
      if (!response.hasError) setConcepts(response.data);
    });
    productGroupsApi.getProductGroups().then((response) => {
      if (!response.hasError)
        setProductGroups(response.data.filter((x) => !x.archived));
    });
    contactsApi
      .getAll({
        ...defaultSearchQuery,
        customerId: article.customerId,
        pageSize: 200,
      })
      .then((response) => {
        if (!response.hasError) setContacts(response.data.items);
      });
  }, [article.customerId]);

  useEffect(() => {
    const refreshConcepts = async () => {
      if (!article.customerId) return;
      const response = await customersApi.getConcepts(article.customerId);
      if (!response.hasError) setConcepts(response.data);
    };

    return reactEvents.conceptCreated.sub(({ id, customerId }) => {
      if (customerId !== article.customerId) return;
      setValue('conceptId', id, { shouldDirty: true });
      refreshConcepts();
    });
  }, [article.customerId, setValue]);

  const save = handleSubmit(async (metadata) => {
    if (!hasPendingChanges) return;
    const response = await request(article.id, metadata);
    if (response.hasError) return;
    markChangesSaved(metadata);
    toast({
      title: t('article.management.detailsSaved'),
      status: 'success',
      position: 'bottom-right',
    });
    angularEvents.articleManagementUpdated.dispatch();
    reactEvents.articleUpdated.dispatch(article.id);
    onUpdated();
  });

  const toggleArchive = async () => {
    const response = await updateLifecycle(article.id);
    if (response.hasError) return;
    toast({
      title: t(
        article.archived ? 'general.entityRestored' : 'general.entityArchived',
        { name: article.name },
      ),
      status: 'success',
      position: 'bottom-right',
    });
    reactEvents.articleUpdated.dispatch(article.id);
    onUpdated();
  };

  return (
    <Box as="form" id="article-management-form" onSubmit={save}>
      <ManagementCard
        title={t('article.management.articleDetails')}
        description={t('article.management.detailsDescription')}
        action={
          <PendingChangesAction
            hasPendingChanges={hasPendingChanges}
            isSaving={loading}
          />
        }
      >
        {error && <ErrorDetails error={error} />}
        <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
          <FormControl>
            <PendingChangesLabel {...getFieldStatus('itemCode')}>
              {t('article.management.itemCode')}
            </PendingChangesLabel>
            <Input
              variant="texas"
              maxLength={article.itemCodeMaxLength ?? 100}
              isDisabled={!!article.syncedVariantId}
              {...register('itemCode')}
            />
            <Text color="articleManagement.mutedText" fontSize="xs" mt={1}>
              {t('article.management.characters', {
                count: itemCode.length,
                maximum: article.itemCodeMaxLength ?? 100,
              })}
            </Text>
          </FormControl>
          <FormControl isInvalid={!!errors.name}>
            <PendingChangesLabel {...getFieldStatus('name')}>
              {t('article.management.articleDescription')}
            </PendingChangesLabel>
            <Input
              variant="texas"
              maxLength={articleDescriptionMaxLength}
              {...register('name', {
                validate: (value) =>
                  !!value.trim() ||
                  t('errors.nameRequired', { name: t('general.description') }),
                maxLength: {
                  value: articleDescriptionMaxLength,
                  message: t('errors.maxLength', {
                    count: articleDescriptionMaxLength,
                  }),
                },
              })}
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            <Text color="articleManagement.mutedText" fontSize="xs" mt={1}>
              {t('article.management.characters', {
                count: description.length,
                maximum: articleDescriptionMaxLength,
              })}
            </Text>
          </FormControl>
          <FormControl>
            <PendingChangesLabel {...getFieldStatus('productGroupId')}>
              {t('article.management.productGroup')}
            </PendingChangesLabel>
            <Controller
              control={control}
              name="productGroupId"
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value ?? ''}
                  onChange={(event) =>
                    field.onChange(
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                >
                  <option value="">
                    {t('article.management.noProductGroup')}
                  </option>
                  {productGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.value}
                    </option>
                  ))}
                </Select>
              )}
            />
          </FormControl>
          <Flex
            align={{ base: 'flex-start', lg: 'center' }}
            direction={{ base: 'column', sm: 'row' }}
            gap={6}
            pt={{ lg: 8 }}
          >
            <Flex align="center" gap={2}>
              <Checkbox {...register('collectionItem')}>
                {t('article.management.collectionItem')}
              </Checkbox>
              <PendingChangesIndicator {...getFieldStatus('collectionItem')} />
            </Flex>
            <Flex align="center" gap={2}>
              <Checkbox {...register('isVariable')}>
                {t('article.management.vdcArticle')}
              </Checkbox>
              <PendingChangesIndicator {...getFieldStatus('isVariable')} />
            </Flex>
          </Flex>
          <FormControl>
            <PendingChangesLabel {...getFieldStatus('conceptId')}>
              {t('article.management.concept')}
            </PendingChangesLabel>
            <Controller
              control={control}
              name="conceptId"
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value ?? ''}
                  onChange={(event) =>
                    field.onChange(
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                >
                  <option value="">{t('article.management.noConcept')}</option>
                  {concepts.map((concept) => (
                    <option key={concept.id} value={concept.id}>
                      {concept.name}
                    </option>
                  ))}
                </Select>
              )}
            />
            <NewConcept
              customerId={article.customerId}
              buttonLabel={t('article.management.createConcept')}
              buttonProps={{ variant: 'formInlineCreate' }}
            />
          </FormControl>
          <FormControl>
            <PendingChangesLabel {...getFieldStatus('contactId')}>
              {t('article.management.defaultBrandContact')}
            </PendingChangesLabel>
            <Controller
              control={control}
              name="contactId"
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value ?? ''}
                  onChange={(event) =>
                    field.onChange(
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                >
                  <option value="">
                    {t('article.management.noDefaultContact')}
                  </option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.name} · {contact.email}
                    </option>
                  ))}
                </Select>
              )}
            />
            <Text color="articleManagement.mutedText" fontSize="xs" mt={1}>
              {t('article.management.contactDescription')}
            </Text>
          </FormControl>
        </Grid>
      </ManagementCard>
      <ArticleBranchesCard article={article} onUpdated={onUpdated} />
      {lifecycleError && (
        <Box mt={5}>
          <ErrorDetails error={lifecycleError} />
        </Box>
      )}
      <Box
        bg="articleManagement.surface"
        border="1px solid"
        borderColor="articleManagement.border"
        mt={5}
        px={6}
        py={5}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Text fontFamily="barlow" fontWeight="700" fontSize="lg">
            {t('article.management.actions')}
          </Text>
          <Text color="articleManagement.mutedText" fontSize="sm">
            {t('article.management.actionsDescription')}
          </Text>
        </Box>
        <Box display="flex" gap={3}>
          <Button variant="outline" onClick={onCopy}>
            {t('article.management.copyArticle')}
          </Button>
          <Button
            variant="outline"
            colorScheme="red"
            onClick={toggleArchive}
            isLoading={lifecycleLoading}
          >
            {t(
              article.archived
                ? 'article.management.restoreArticle'
                : 'article.management.archiveArticle',
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
